import { useCallback, useEffect } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';

import { resetFormState, useEdit, useInvalidate, usePostAuditLog, useProfileCrudActions } from '@/_shared';

import { useRevisionHistory } from './use-revision-history';

import { useDeleteMutation, useGetCompetitor, useUpdateMutation } from '../apis';
import { competitorFormToUpdate, competitorQueryToForm } from '../converters';
import { useFormDefinition } from '../form-context';
import type { CompetitorFormSchema, CompetitorQuerySchema, CompetitorUpdateSchema } from '../schemas';

export function usePage() {
  const nav = useNavigate();
  const edit = useEdit();
  const form = useFormDefinition();
  const { id } = useParams({ strict: false });
  usePostAuditLog({ endpoint: 'competitors', id: `${id}` });
  const { setHasToInvalidate, removeQuery } = useInvalidate(['COMPETITOR', id ?? '']);

  const competitor = useGetCompetitor(`${id}`);
  const updateMutation = useUpdateMutation(`${id}`);
  const deleteMutation = useDeleteMutation();

  const handleSuccessSideEffects = useCallback(() => {
    removeQuery(['COMPETITORS']);
    setHasToInvalidate.setTrue();
  }, [removeQuery, setHasToInvalidate]);

  const { onUpdate, onPartialUpdate, onDelete, isDeleting, isPending, isSuccess } = useProfileCrudActions<
    CompetitorFormSchema,
    CompetitorUpdateSchema,
    { data: CompetitorQuerySchema }
  >({
    form,
    edit,
    formToMutation: competitorFormToUpdate,
    updateMutation,
    deleteMutation,
    deleteVariables: { id: `${id}` },
    updateSuccessMessage: 'Competitor has been updated.',
    deleteSuccessMessage: 'Competitor has been deleted.',
    onSuccessSideEffects: handleSuccessSideEffects,
    onDeleteSuccess: () => {
      nav({ to: '/competitors' });
    },
  });

  const onSubmit = useCallback(
    async (data: CompetitorFormSchema) => {
      onUpdate(competitorFormToUpdate(data));
    },
    [onUpdate]
  );

  const revisionHistory = useRevisionHistory(form, onUpdate);

  useEffect(() => {
    const { data: response } = competitor;
    if (response) {
      resetFormState(form, competitorQueryToForm(response.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitor.data]);

  console.log(form.formState.errors);

  return {
    id,
    form,
    edit,
    isPending,
    isSuccess,
    isDeleting,
    onSubmit,
    onDelete,
    onPartialUpdate,
    revisionHistory,
    isReverting: competitor.isFetching || isPending,
  };
}

export type UsePageType = ReturnType<typeof usePage>;
